import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { IntentLauncher, IntentAction } from '../../components/overview/IntentLauncher';
import { StatCard } from '../../components/overview/StatCard';
import { Reveal } from '../../components/overview/Reveal';
import { useParallax } from '../../hooks/useParallax';

/**
 * Operator System Admin console (/platform/admin, PlatformAdmin only).
 *
 * The operator's "mission control" for the platform itself — distinct from the
 * per-entity management pages (orgs/events/venues/operators). It surfaces
 * system-level signals and controls behind an intent-based flow:
 *   - a live health pulse band (DB + scale, polled)
 *   - intent verbs that jump to the relevant panel or page
 *   - a bento of platform-scale metrics
 *   - the module catalog with adoption
 *   - a data-integrity board (deterministic drift probes)
 *
 * It reuses the operator design language (indigo hero, parallax glow, intent
 * launcher, StatCard, Reveal) so it feels native to the console.
 */

interface SystemHealth {
  database: 'connected' | 'disconnected';
  checkedAt: string;
  scale: {
    tenants: number;
    events: number;
    users: number;
    guests: number;
    venues: number;
    orders: number;
    tickets: number;
  };
  integrity: {
    key: string;
    label: string;
    detail: string;
    count: number;
    severity: 'ok' | 'warning' | 'critical';
  }[];
}

interface Capabilities {
  totalTenants: number;
  modules: {
    key: string;
    label: string;
    description: string;
    icon: string;
    isBase: boolean;
    dependsOn: string[];
    tenantsEnabled: number;
  }[];
}

interface LoginAudit {
  summary: {
    total24h: number;
    success24h: number;
    failure24h: number;
    failureReasons: { reason: string; count: number }[];
    topLocations: { location: string; count: number }[];
    devices: { mobile: number; desktop: number };
    uniqueIps: number;
    timeline: { hour: string; success: number; failure: number }[];
    suspiciousIps: { ip: string; failures: number }[];
  };
  recent: {
    data: {
      id: string;
      identifier: string;
      fullName: string | null;
      outcome: string;
      failureReason: string | null;
      ipAddress: string;
      city: string | null;
      country: string | null;
      userAgent: string;
      timestamp: string;
    }[];
    total: number;
    page: number;
    pageSize: number;
  };
}

function deviceLabel(ua: string): string {
  if (/mobile|android|iphone/i.test(ua)) return '📱 Mobile';
  if (/ipad|tablet/i.test(ua)) return '📱 Tablet';
  return '💻 Desktop';
}
function fmtTime(iso: string): string {
  try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function fmtClock(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return '—';
  }
}

export function PlatformAdminConsolePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { offset, onMouseMove, onMouseLeave } = useParallax(20);

  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [caps, setCaps] = useState<Capabilities | null>(null);
  const [audit, setAudit] = useState<LoginAudit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Login-audit filters + pagination (recent-attempts table).
  const [auditOutcome, setAuditOutcome] = useState<'' | 'success' | 'failure'>('');
  const [auditQuery, setAuditQuery] = useState('');
  const [auditRows, setAuditRows] = useState<LoginAudit['recent']['data']>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditBusy, setAuditBusy] = useState(false);
  const AUDIT_PAGE_SIZE = 20;

  // Load (or append) a page of recent attempts with the current filters.
  async function loadAuditPage(page: number, append: boolean) {
    setAuditBusy(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(AUDIT_PAGE_SIZE) });
      if (auditOutcome) params.set('outcome', auditOutcome);
      if (auditQuery.trim()) params.set('identifier', auditQuery.trim());
      const res = await apiClient<LoginAudit>(`/platform/login-audit?${params.toString()}`);
      setAuditTotal(res.recent.total);
      setAuditPage(page);
      setAuditRows((prev) => (append ? [...prev, ...res.recent.data] : res.recent.data));
      // Keep the summary fresh from the same response.
      setAudit(res);
    } catch { /* keep prior */ }
    finally { setAuditBusy(false); }
  }

  const modulesRef = useRef<HTMLDivElement | null>(null);
  const integrityRef = useRef<HTMLDivElement | null>(null);
  const loginRef = useRef<HTMLDivElement | null>(null);

  async function loadHealth() {
    const h = await apiClient<SystemHealth>('/platform/system-health');
    setHealth(h);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [h, c, la] = await Promise.all([
          apiClient<SystemHealth>('/platform/system-health'),
          apiClient<Capabilities>('/platform/capabilities'),
          apiClient<LoginAudit>('/platform/login-audit').catch(() => null),
        ]);
        if (!cancelled) {
          setHealth(h); setCaps(c); setAudit(la);
          if (la) { setAuditRows(la.recent.data); setAuditTotal(la.recent.total); setAuditPage(la.recent.page); }
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load system admin console');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live pulse: re-check health every 30s so the band feels alive.
  useEffect(() => {
    const id = setInterval(() => { loadHealth().catch(() => { /* keep prior */ }); }, 30000);
    return () => clearInterval(id);
  }, []);

  const firstName = (user?.fullName || '').split(' ')[0] || 'operator';

  function scrollTo(ref: React.RefObject<HTMLDivElement | null>) {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const intents: IntentAction[] = [
    {
      key: 'modules',
      label: 'Configure capabilities',
      description: 'The module catalog and how widely each is adopted.',
      icon: '🧩',
      gradient: ['#6366f1', '#8b5cf6'],
      onClick: () => scrollTo(modulesRef),
      featured: true,
    },
    {
      key: 'integrity',
      label: 'Inspect data integrity',
      description: 'Deterministic drift probes across every tenant.',
      icon: '🩺',
      gradient: ['#0ea5e9', '#2563eb'],
      onClick: () => scrollTo(integrityRef),
    },
    {
      key: 'login-audit',
      label: 'Audit logins',
      description: 'Who signed in, from where, and what failed.',
      icon: '🔑',
      gradient: ['#ec4899', '#be185d'],
      onClick: () => scrollTo(loginRef),
    },
    {
      key: 'operators',
      label: 'Manage operators',
      description: 'Platform staff, roles, and access.',
      icon: '🛡️',
      gradient: ['#10b981', '#059669'],
      onClick: () => navigate('/platform/operators'),
    },
    {
      key: 'orgs',
      label: 'Govern organizations',
      description: 'Status, users, and tenant configuration.',
      icon: '🏢',
      gradient: ['#f59e0b', '#ea580c'],
      onClick: () => navigate('/platform/organizations'),
    },
  ];

  const dbOk = health?.database === 'connected';
  const criticalCount = (health?.integrity ?? []).filter((i) => i.severity === 'critical').length;
  const warnCount = (health?.integrity ?? []).filter((i) => i.severity === 'warning').length;
  const systemState: { label: string; tone: string; dot: string } = !health
    ? { label: 'Checking…', tone: '#c7d2fe', dot: '#a5b4fc' }
    : !dbOk
      ? { label: 'Database unreachable', tone: '#fecaca', dot: '#ef4444' }
      : criticalCount > 0
        ? { label: `${criticalCount} critical signal${criticalCount === 1 ? '' : 's'}`, tone: '#fed7aa', dot: '#f59e0b' }
        : warnCount > 0
          ? { label: `${warnCount} advisory`, tone: '#fde68a', dot: '#eab308' }
          : { label: 'All systems nominal', tone: '#bbf7d0', dot: '#34d399' };

  return (
    <div>
      <style>{`
        @keyframes ep-admin-pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.45; transform: scale(0.7); } }
        @media (max-width: 820px) {
          .ep-admin-bento { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 560px) {
          .ep-admin-bento { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* Hero band */}
      <div style={styles.hero} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
        <span style={{ ...styles.heroGlow, transform: `translate(${offset.x}px, ${offset.y}px)` }} aria-hidden />
        <div style={styles.heroInner}>
          <div style={styles.kicker}>System Admin · Mission Control</div>
          <h1 style={styles.heroTitle}>{greeting()}, {firstName}.</h1>
          <p style={styles.heroSub}>
            The whole platform, from the inside. Health, capabilities, and integrity — in one console.
          </p>

          {/* Live pulse band */}
          <div style={styles.pulseBand}>
            <span style={styles.pulseItem}>
              <span style={{ ...styles.pulseDot, background: systemState.dot, animation: 'ep-admin-pulse 1.8s ease-in-out infinite' }} aria-hidden />
              <span style={{ color: systemState.tone, fontWeight: 800 }}>{systemState.label}</span>
            </span>
            <span style={styles.pulseSep} aria-hidden>·</span>
            <span style={styles.pulseMeta}>DB {dbOk ? 'connected' : health ? 'down' : '…'}</span>
            {health && (
              <>
                <span style={styles.pulseSep} aria-hidden>·</span>
                <span style={styles.pulseMeta}>checked {fmtClock(health.checkedAt)}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Intent launcher */}
      <IntentLauncher actions={intents} />

      {loading ? (
        <div style={styles.muted}>Loading system telemetry…</div>
      ) : health ? (
        <>
          {/* Scale bento */}
          <div style={styles.bento} className="ep-admin-bento">
            <Reveal index={0} style={{ display: 'flex' }}>
              <StatCard label="Organizations" value={health.scale.tenants} icon="🏢" accent="#6366f1" drillLabel="Govern" onClick={() => navigate('/platform/organizations')} />
            </Reveal>
            <Reveal index={1} style={{ display: 'flex' }}>
              <StatCard label="Events" value={health.scale.events} icon="📅" accent="#0ea5e9" drillLabel="Browse" onClick={() => navigate('/platform/events')} />
            </Reveal>
            <Reveal index={2} style={{ display: 'flex' }}>
              <StatCard label="Users" value={health.scale.users} icon="👤" accent="#10b981" hint={`${health.scale.guests.toLocaleString()} guests`} />
            </Reveal>
            <Reveal index={3} style={{ display: 'flex' }}>
              <StatCard label="Venues" value={health.scale.venues} icon="📍" accent="#f59e0b" drillLabel="Manage" onClick={() => navigate('/platform/venues')} />
            </Reveal>
            <Reveal index={4} style={{ display: 'flex' }}>
              <StatCard label="Orders" value={health.scale.orders} icon="🧾" accent="#8b5cf6" />
            </Reveal>
            <Reveal index={5} style={{ display: 'flex' }}>
              <StatCard label="Active tickets" value={health.scale.tickets} icon="🎟️" accent="#ec4899" />
            </Reveal>
          </div>

          {/* Module catalog */}
          <section ref={modulesRef} style={styles.card}>
            <div style={styles.cardHead}>
              <div>
                <h2 style={styles.h2}>🧩 Capability catalog</h2>
                <p style={styles.cardSub}>Every module the platform offers, and how many organizations have it on.</p>
              </div>
              <span style={styles.cardCta} onClick={() => navigate('/platform/capabilities')}>Full view →</span>
            </div>
            {!caps ? (
              <div style={styles.muted}>Catalog unavailable.</div>
            ) : (
              <div style={styles.moduleGrid}>
                {caps.modules.map((m) => {
                  const pct = caps.totalTenants ? Math.round((m.tenantsEnabled / caps.totalTenants) * 100) : 0;
                  return (
                    <div key={m.key} style={styles.moduleCard}>
                      <div style={styles.moduleTop}>
                        <span style={styles.moduleIcon}>{m.icon}</span>
                        <span style={styles.moduleName}>{m.label}</span>
                        {m.isBase && <span style={styles.baseTag}>base</span>}
                      </div>
                      <p style={styles.moduleDesc}>{m.description}</p>
                      <div style={styles.moduleTrack}>
                        <div style={{ ...styles.moduleFill, width: `${pct}%` }} />
                      </div>
                      <div style={styles.moduleFoot}>
                        <span>{m.tenantsEnabled}/{caps.totalTenants} orgs</span>
                        <span style={{ fontWeight: 800, color: '#4f46e5' }}>{pct}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Data integrity board */}
          <section ref={integrityRef} style={styles.card}>
            <div style={styles.cardHead}>
              <div>
                <h2 style={styles.h2}>🩺 Data integrity</h2>
                <p style={styles.cardSub}>Deterministic drift probes across all tenants. Zero is healthy.</p>
              </div>
            </div>
            <div style={styles.integrityList}>
              {health.integrity.map((it) => {
                const unavailable = it.count < 0;
                const tone =
                  unavailable ? INTEGRITY_TONE.warning
                  : it.severity === 'critical' ? INTEGRITY_TONE.critical
                  : it.severity === 'warning' && it.count > 0 ? INTEGRITY_TONE.warning
                  : INTEGRITY_TONE.ok;
                return (
                  <div key={it.key} style={{ ...styles.integrityRow, borderLeft: `3px solid ${tone.bar}` }}>
                    <div style={styles.integrityMeta}>
                      <div style={styles.integrityLabel}>{it.label}</div>
                      <div style={styles.integrityDetail}>{it.detail}</div>
                    </div>
                    <div style={{ ...styles.integrityCount, background: tone.bg, color: tone.fg }}>
                      {unavailable ? 'n/a' : it.count === 0 ? '✓ clear' : it.count.toLocaleString()}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Observability — Login audit */}
          <section ref={loginRef} style={styles.card}>
            <div style={styles.cardHead}>
              <div>
                <h2 style={styles.h2}>🔑 Login audit</h2>
                <p style={styles.cardSub}>Cross-tenant sign-in telemetry over the last 24 hours, plus recent attempts.</p>
              </div>
            </div>
            {!audit ? (
              <div style={styles.muted}>Login telemetry unavailable.</div>
            ) : (
              <>
                {/* 24h summary metrics */}
                <div style={styles.auditMetrics}>
                  <AuditMetric label="Attempts (24h)" value={audit.summary.total24h} />
                  <AuditMetric label="Successful" value={audit.summary.success24h} tone="#10b981" />
                  <AuditMetric label="Failed" value={audit.summary.failure24h} tone={audit.summary.failure24h > 0 ? '#ef4444' : undefined} />
                  <AuditMetric label="Unique IPs" value={audit.summary.uniqueIps} />
                  <AuditMetric label="Mobile" value={audit.summary.devices.mobile} />
                  <AuditMetric label="Desktop" value={audit.summary.devices.desktop} />
                </div>

                {/* 24h timeline bar chart */}
                <div style={styles.timelineWrap}>
                  <div style={styles.subLabel}>24-hour activity</div>
                  <div style={styles.timeline}>
                    {audit.summary.timeline.map((t, i) => {
                      const max = Math.max(1, ...audit.summary.timeline.map((x) => x.success + x.failure));
                      const total = t.success + t.failure;
                      const h = Math.round((total / max) * 46);
                      return (
                        <div key={i} style={styles.timelineCol} title={`${t.hour}: ${t.success} ok, ${t.failure} failed`}>
                          <div style={styles.timelineBarStack}>
                            {t.failure > 0 && <div style={{ height: `${Math.round((t.failure / Math.max(total, 1)) * h)}px`, background: '#ef4444', borderRadius: '2px 2px 0 0' }} />}
                            {t.success > 0 && <div style={{ height: `${Math.round((t.success / Math.max(total, 1)) * h)}px`, background: '#10b981' }} />}
                            {total === 0 && <div style={{ height: 2, background: '#e5e7eb' }} />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Breakdown row: failure reasons, locations, suspicious IPs */}
                <div style={styles.breakdownRow}>
                  <div style={styles.breakdownCol}>
                    <div style={styles.subLabel}>Failure reasons</div>
                    {audit.summary.failureReasons.length === 0 ? (
                      <div style={styles.okNote}>No failures 🎉</div>
                    ) : audit.summary.failureReasons.map((f) => (
                      <div key={f.reason} style={styles.bdRow}><span>{f.reason.replace(/_/g, ' ')}</span><strong>{f.count}</strong></div>
                    ))}
                  </div>
                  <div style={styles.breakdownCol}>
                    <div style={styles.subLabel}>Top locations</div>
                    {audit.summary.topLocations.length === 0 ? (
                      <div style={styles.muted}>No data.</div>
                    ) : audit.summary.topLocations.map((l) => (
                      <div key={l.location} style={styles.bdRow}><span>{l.location}</span><strong>{l.count}</strong></div>
                    ))}
                  </div>
                  <div style={styles.breakdownCol}>
                    <div style={styles.subLabel}>Suspicious IPs (3+ fails)</div>
                    {audit.summary.suspiciousIps.length === 0 ? (
                      <div style={styles.okNote}>None flagged ✓</div>
                    ) : audit.summary.suspiciousIps.map((s) => (
                      <div key={s.ip} style={{ ...styles.bdRow, color: '#991b1b' }}><code>{s.ip}</code><strong>{s.failures}</strong></div>
                    ))}
                  </div>
                </div>

                {/* Recent attempts table + filters */}
                <div style={{ marginTop: 16 }}>
                  <div style={styles.auditControls}>
                    <div style={styles.subLabel}>Recent attempts ({auditTotal.toLocaleString()} total)</div>
                    <div style={styles.auditFilters}>
                      <input
                        style={styles.auditSearch}
                        placeholder="Search identifier…"
                        value={auditQuery}
                        onChange={(e) => setAuditQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') loadAuditPage(1, false); }}
                        aria-label="Search login identifier"
                      />
                      {(['', 'success', 'failure'] as const).map((o) => (
                        <button
                          key={o || 'all'}
                          style={auditOutcome === o ? styles.filterBtnActive : styles.filterBtn}
                          onClick={() => { setAuditOutcome(o); setTimeout(() => loadAuditPage(1, false), 0); }}
                          aria-pressed={auditOutcome === o}
                        >
                          {o === '' ? 'All' : o === 'success' ? 'Success' : 'Failed'}
                        </button>
                      ))}
                      <button style={styles.filterBtn} onClick={() => loadAuditPage(1, false)} disabled={auditBusy}>
                        {auditBusy ? '…' : 'Apply'}
                      </button>
                    </div>
                  </div>
                  <table style={styles.auditTable}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Time</th>
                        <th style={styles.th}>Identifier</th>
                        <th style={styles.th}>Outcome</th>
                        <th style={styles.th}>Location</th>
                        <th style={styles.th}>IP</th>
                        <th style={styles.th}>Device</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditRows.length === 0 ? (
                        <tr><td colSpan={6} style={styles.emptyCell}>No login attempts match.</td></tr>
                      ) : auditRows.map((r) => (
                        <tr key={r.id}>
                          <td style={styles.td}>{fmtTime(r.timestamp)}</td>
                          <td style={styles.td}>
                            <div style={{ fontWeight: 700, color: INK }}>{r.identifier}</div>
                            {r.fullName && <div style={styles.auditSub}>{r.fullName}</div>}
                          </td>
                          <td style={styles.td}>
                            {r.outcome === 'success'
                              ? <span style={{ ...styles.outcomePill, background: '#dcfce7', color: '#166534' }}>success</span>
                              : <span style={{ ...styles.outcomePill, background: '#fee2e2', color: '#991b1b' }}>{r.failureReason ? r.failureReason.replace(/_/g, ' ') : 'failed'}</span>}
                          </td>
                          <td style={styles.td}>{[r.city, r.country].filter(Boolean).join(', ') || '—'}</td>
                          <td style={styles.td}><code style={styles.ipCode}>{r.ipAddress}</code></td>
                          <td style={styles.td}>{deviceLabel(r.userAgent)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {auditRows.length < auditTotal && (
                    <div style={styles.loadMoreWrap}>
                      <button style={styles.loadMoreBtn} onClick={() => loadAuditPage(auditPage + 1, true)} disabled={auditBusy}>
                        {auditBusy ? 'Loading…' : `Load next ${AUDIT_PAGE_SIZE}`}
                      </button>
                      <span style={styles.loadMoreCount}>Showing {auditRows.length} of {auditTotal.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}

function AuditMetric({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div style={styles.auditMetric}>
      <div style={{ ...styles.auditMetricValue, ...(tone ? { color: tone } : {}) }}>{value.toLocaleString()}</div>
      <div style={styles.auditMetricLabel}>{label}</div>
    </div>
  );
}

const INTEGRITY_TONE = {
  ok: { bar: '#10b981', bg: '#dcfce7', fg: '#166534' },
  warning: { bar: '#f59e0b', bg: '#fef3c7', fg: '#92400e' },
  critical: { bar: '#ef4444', bg: '#fee2e2', fg: '#991b1b' },
};

const ACCENT = '#4f46e5';
const BORDER = '#e8eaf0';
const MUTED = '#6b7280';
const INK = '#0b1120';

const styles: Record<string, React.CSSProperties> = {
  hero: { position: 'relative', overflow: 'hidden', borderRadius: 20, padding: '28px', marginBottom: 24, background: 'linear-gradient(120deg, #0b1120 0%, #1e1b4b 55%, #312e81 100%)', color: '#fff' },
  heroGlow: { position: 'absolute', top: -80, right: -40, width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,92,246,0.55), transparent 70%)', filter: 'blur(10px)', pointerEvents: 'none', transition: 'transform 0.25s cubic-bezier(.2,.8,.2,1)' },
  heroInner: { position: 'relative', zIndex: 1 },
  kicker: { fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#a5b4fc' },
  heroTitle: { fontSize: '2rem', fontWeight: 800, margin: '8px 0 6px', letterSpacing: '-0.02em' },
  heroSub: { fontSize: '0.95rem', color: '#c7d2fe', margin: 0, maxWidth: 560, lineHeight: 1.5 },

  pulseBand: { display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 18, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 999, padding: '8px 16px', backdropFilter: 'blur(4px)' },
  pulseItem: { display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' },
  pulseDot: { width: 9, height: 9, borderRadius: '50%', display: 'inline-block' },
  pulseSep: { color: 'rgba(199,210,254,0.5)' },
  pulseMeta: { fontSize: '0.8rem', color: '#c7d2fe', fontVariantNumeric: 'tabular-nums' },

  bento: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 22 },

  card: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 16, padding: 18, boxShadow: '0 1px 3px rgba(16,24,40,0.04)', marginBottom: 20, scrollMarginTop: 20 },
  cardHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, gap: 12 },
  cardSub: { fontSize: '0.82rem', color: MUTED, margin: '4px 0 0' },
  cardCta: { color: ACCENT, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap' },
  h2: { fontSize: '1.1rem', fontWeight: 800, margin: 0, color: INK },

  moduleGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 },
  moduleCard: { border: `1px solid ${BORDER}`, borderRadius: 12, padding: 14, background: 'linear-gradient(135deg, #ffffff, #f6f7ff)' },
  moduleTop: { display: 'flex', alignItems: 'center', gap: 8 },
  moduleIcon: { fontSize: '1.15rem' },
  moduleName: { fontWeight: 800, color: INK, fontSize: '0.9rem', flex: 1 },
  baseTag: { fontSize: '0.6rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#4f46e5', background: '#eef2ff', borderRadius: 999, padding: '2px 7px' },
  moduleDesc: { fontSize: '0.76rem', color: MUTED, margin: '8px 0 12px', lineHeight: 1.4, minHeight: 32 },
  moduleTrack: { height: 8, background: '#eef2ff', borderRadius: 999, overflow: 'hidden' },
  moduleFill: { height: '100%', background: `linear-gradient(90deg, ${ACCENT}, #8b5cf6)`, borderRadius: 999 },
  moduleFoot: { display: 'flex', justifyContent: 'space-between', marginTop: 7, fontSize: '0.74rem', color: MUTED, fontVariantNumeric: 'tabular-nums' },

  integrityList: { display: 'flex', flexDirection: 'column', gap: 8 },
  integrityRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, padding: '12px 14px', background: '#f9fafb', borderRadius: 10 },
  integrityMeta: { minWidth: 0 },
  integrityLabel: { fontWeight: 700, color: INK, fontSize: '0.9rem' },
  integrityDetail: { fontSize: '0.78rem', color: MUTED, marginTop: 2 },
  integrityCount: { flexShrink: 0, borderRadius: 999, padding: '5px 14px', fontSize: '0.85rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },

  muted: { color: MUTED, padding: '20px 4px' },
  error: { background: '#fef2f2', border: '1px solid #fca5a5', color: '#7f1d1d', borderRadius: 8, padding: '10px 14px', marginBottom: 16 },

  auditMetrics: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 16 },
  auditMetric: { background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 12, padding: '12px 14px' },
  auditMetricValue: { fontSize: '1.5rem', fontWeight: 800, color: INK, lineHeight: 1, fontVariantNumeric: 'tabular-nums' },
  auditMetricLabel: { fontSize: '0.7rem', color: MUTED, fontWeight: 700, marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.04em' },

  timelineWrap: { marginBottom: 16 },
  subLabel: { fontSize: '0.72rem', color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 },
  timeline: { display: 'flex', alignItems: 'flex-end', gap: 3, height: 54, background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 10, padding: '4px 6px' },
  timelineCol: { flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', height: '100%' },
  timelineBarStack: { display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', width: '70%', minWidth: 4 },

  breakdownRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 },
  breakdownCol: { background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 12, padding: 14 },
  bdRow: { display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: '0.82rem', color: '#334155', padding: '4px 0', borderBottom: '1px solid #eef2f7' },
  okNote: { fontSize: '0.82rem', color: '#166534', fontWeight: 600 },

  auditTable: { width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' },
  auditSub: { fontSize: '0.72rem', color: MUTED },
  outcomePill: { display: 'inline-block', borderRadius: 999, padding: '2px 9px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'capitalize' },
  ipCode: { background: '#f1f5f9', borderRadius: 5, padding: '2px 6px', fontSize: '0.78rem' },
  emptyCell: { padding: '18px', textAlign: 'center', color: MUTED, fontSize: '0.85rem' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: `2px solid ${BORDER}`, color: MUTED, fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
  td: { padding: '10px', borderBottom: '1px solid #f0f1f5', verticalAlign: 'top', color: '#334155' },

  auditControls: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 },
  auditFilters: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  auditSearch: { padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.82rem', minWidth: 180 },
  filterBtn: { background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 8, padding: '6px 12px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' },
  filterBtnActive: { background: ACCENT, color: '#fff', border: `1px solid ${ACCENT}`, borderRadius: 8, padding: '6px 12px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' },
  loadMoreWrap: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 },
  loadMoreBtn: { background: '#eef2ff', color: ACCENT, border: '1px solid #c7d2fe', borderRadius: 8, padding: '8px 16px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' },
  loadMoreCount: { fontSize: '0.78rem', color: MUTED },
};
